// ─── AppHeader ────────────────────────────────────────────────────────────────
// Shared white header strip — same look + dropdown behaviour as the header
// inside DashboardPage (Logo · Dashboard / Data Analytics / Report / Gallery
// / Settings / Live · As of <time>), made standalone so non-Dashboard pages
// (Account Settings, etc.) render the same chrome without pulling in
// DashboardPage's project-specific local state.
//
// Dropdowns mirror DashboardPage:
//   Dashboard       → project templates (Overview, …) + "Config Dashboards"
//   Data Analytics  → Visualize · Validate · Formulas
//   Report          → Field Station Turbidity · Select Report
//   Gallery         → no dropdown — single click navigates
//   Settings        → Users · Project
//   Live            → Data Logger · Communications · Sensor Readings
//
// Clicking a dropdown item persists the relevant tab + sub-index to
// localStorage (same keys DashboardPage reads on mount) and navigates the
// user to /dashboard/project/<id> so they land on the right tab and the
// right section. We pick the project to navigate to by reading the
// last-active id from localStorage, falling back to the first project the
// projects API returns. Project list + the chosen project's templates are
// fetched once on mount so the dropdowns are populated by the time the
// user opens them.
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { AppHeaderShell } from './AppHeaderShell'
import useAuthStore from '../store/auth-store'
import { resolveIanaTz } from '../lib/tz'
import { useHeaderNav, NAV_TABS_WITH_DROPDOWN } from '../lib/useHeaderNav'

// Tab list, sub-items, project resolution and the deep-link rules all live
// in useHeaderNav so the mobile drawer drives the same menu.
const TABS_WITH_DROPDOWN = NAV_TABS_WITH_DROPDOWN

export function AppHeader({ activeTab = null }) {
  const { dropdownConfig, goToDashboard } = useHeaderNav()
  const rawTz = useAuthStore((s) => s.user?.timezone)
  const tz    = resolveIanaTz(rawTz)

  const [now, setNow]                 = useState(() => new Date())
  const [hoveredTab, setHoveredTab]   = useState(null)
  const closeTimer                    = useRef(null)

  // Tick the "As of" timestamp every 30 s so the header stays fresh.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30 * 1000)
    return () => clearInterval(id)
  }, [])

  const nowLabel = (() => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short',
      }).format(now)
    } catch { return now.toString() }
  })()

  // Closing the hover menu is this header's own concern; the navigate
  // itself comes from the shared hook.
  const goTo = (args) => { setHoveredTab(null); goToDashboard(args) }

  const openMenu  = (tab) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
    if (TABS_WITH_DROPDOWN.has(tab)) setHoveredTab(tab)
  }
  const closeMenu = () => {
    // Tiny grace period so the cursor can travel from tab → dropdown without
    // the menu collapsing under it.
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setHoveredTab(null), 80)
  }

  // Frame + styling come from AppHeaderShell (shared with DashboardPage's
  // header, so the two can't drift apart again). Only this page's behaviour —
  // route-based navigation with a hover grace period — lives here.
  return (
    <AppHeaderShell
      activeTab={activeTab}
      openTab={hoveredTab}
      nowLabel={nowLabel}
      onTabEnter={openMenu}
      onTabLeave={closeMenu}
      onTabClick={(tab) => goTo({ tab })}
      renderDropdown={(tab) => {
        if (!TABS_WITH_DROPDOWN.has(tab) || hoveredTab !== tab) return null
        return (
          <div
            className="absolute top-full left-[-20px] mt-0.5 bg-white border border-gray-100 rounded-lg shadow-xl z-50 min-w-[250px] py-1.5 overflow-hidden"
            onMouseEnter={() => openMenu(tab)}
            onMouseLeave={closeMenu}
          >
            {dropdownConfig[tab].items.map((item, idx) => {
              // Most tabs carry plain-string items; the Report tab carries
              // { label, report } objects (dynamic saved reports). Render the
              // label either way.
              const itemLabel = typeof item === 'string' ? item : item.label
              const isConfigItem = tab === 'Dashboard' && (itemLabel === 'Config Dashboards' || itemLabel === 'Configure Dashboards')
              return (
                <div key={`${tab}-${idx}-${itemLabel}`}>
                  {isConfigItem && <div className="border-t border-gray-200 my-1.5" />}
                  <button
                    onClick={() => goTo({
                      tab, indexKey: dropdownConfig[tab].indexKey, indexValue: idx,
                    })}
                    className="common-size15 w-full text-left px-6 py-2 text-sm text-[#4D4D4D] hover:bg-gray-50 transition"
                  >
                    {itemLabel}
                  </button>
                </div>
              )
            })}
          </div>
        )
      }}
    />
  )
}
